import { useState } from "react";

import {
  ChevronDown,
  ChevronUp,
  Clock,
  Edit,
  Mail,
  MessageSquare,
  PlusCircle,
  Save,
  Trash2,
} from "lucide-react";
import Swal from "sweetalert2";

import EmailTemplateBuilder from "~/components/email/EmailTemplateBuilder";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "~/components/ui/accordion";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Switch } from "~/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";
import { Textarea } from "~/components/ui/textarea";
import { toast } from "~/components/ui/use-toast";
import { cn } from "~/lib/utils";

// Types
interface ReminderRuleTime {
  hour: number;
  minute: number;
}

interface ReminderRule {
  id: string;
  daysFromDue: number; // Negative for before due, positive for after due
  isActive: boolean;
  channels: {
    email: {
      enabled: boolean;
      subject: string;
      body: string;
    };
    whatsapp: {
      enabled: boolean;
      message: string;
    };
  };
  sendTime: ReminderRuleTime;
  createdAt: Date;
  updatedAt: Date;
}

// Mock data for initial rules
const initialRules: ReminderRule[] = [
  {
    id: "rule-1",
    daysFromDue: -10, // 10 days before due
    isActive: true,
    channels: {
      email: {
        enabled: true,
        subject: "Recordatorio de pago próximo",
        body: "Estimado cliente,\n\nEste es un recordatorio amistoso de que tienes un pago que vence en 10 días.\n\nSaludos cordiales,\nEquipo Financiero",
      },
      whatsapp: {
        enabled: false,
        message: "",
      },
    },
    sendTime: { hour: 9, minute: 0 }, // 9:00 AM
    createdAt: new Date("2023-01-01"),
    updatedAt: new Date("2023-01-01"),
  },
  {
    id: "rule-2",
    daysFromDue: -3, // 3 days before due
    isActive: true,
    channels: {
      email: {
        enabled: true,
        subject: "Recordatorio importante: Pago pendiente",
        body: "Estimado cliente,\n\nEste es un recordatorio de que tienes un pago que vence en 3 días. Por favor asegúrate de procesarlo a tiempo para evitar cargos por mora.\n\nSaludos cordiales,\nEquipo Financiero",
      },
      whatsapp: {
        enabled: true,
        message:
          "Recordatorio: Tienes un pago que vence en 3 días. Por favor procésalo a tiempo para evitar cargos por mora.",
      },
    },
    sendTime: { hour: 10, minute: 30 }, // 10:30 AM
    createdAt: new Date("2023-01-10"),
    updatedAt: new Date("2023-01-15"),
  },
  {
    id: "rule-3",
    daysFromDue: 1, // 1 day after due
    isActive: true,
    channels: {
      email: {
        enabled: true,
        subject: "Pago vencido - Acción requerida",
        body: "Estimado cliente,\n\nTe informamos que tu pago venció ayer. Por favor procede a pagarlo lo antes posible para evitar cargos adicionales por mora.\n\nSaludos cordiales,\nEquipo Financiero",
      },
      whatsapp: {
        enabled: true,
        message:
          "IMPORTANTE: Tu pago venció ayer. Por favor procede a pagarlo lo antes posible para evitar cargos adicionales por mora.",
      },
    },
    sendTime: { hour: 8, minute: 0 }, // 8:00 AM
    createdAt: new Date("2023-02-01"),
    updatedAt: new Date("2023-02-01"),
  },
];

export function PaymentReminderRulesSection() {
  const [rules, setRules] = useState<ReminderRule[]>(initialRules);
  const [expandedRuleId, setExpandedRuleId] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState<string | null>(null);
  const [showNewRuleForm, setShowNewRuleForm] = useState(false);

  // New rule form state
  const [newRule, setNewRule] = useState<
    Omit<ReminderRule, "id" | "createdAt" | "updatedAt">
  >({
    daysFromDue: -7,
    isActive: true,
    channels: {
      email: {
        enabled: true,
        subject: "Recordatorio de pago",
        body: "Estimado cliente,\n\nEste es un recordatorio de tu pago próximo.\n\nSaludos cordiales,\nEquipo Financiero",
      },
      whatsapp: {
        enabled: false,
        message: "Recordatorio: Tienes un pago próximo.",
      },
    },
    sendTime: { hour: 9, minute: 0 },
  });

  // Toggle rule expansion
  const toggleRuleExpansion = (ruleId: string) => {
    setExpandedRuleId(expandedRuleId === ruleId ? null : ruleId);
    setIsEditing(null); // Close editing when collapsing
  };

  // Start editing a rule
  const startEditing = (ruleId: string) => {
    setIsEditing(ruleId);
    setExpandedRuleId(ruleId); // Expand when editing
  };

  // Save edited rule
  const saveRule = (ruleId: string) => {
    setIsEditing(null);
    toast({
      title: "Regla guardada",
      description:
        "La regla de recordatorio ha sido actualizada correctamente.",
    });
  };

  // Delete a rule
  const deleteRule = (ruleId: string) => {
    Swal.fire({
      title: "¿Estás seguro?",
      text: "Esta acción eliminará permanentemente esta regla de recordatorio de pago.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
      reverseButtons: true,
    }).then((result) => {
      if (result.isConfirmed) {
        setRules(rules.filter((rule) => rule.id !== ruleId));
        toast({
          title: "Regla eliminada",
          description:
            "La regla de recordatorio ha sido eliminada correctamente.",
        });
      }
    });
  };

  // Add new rule
  const addNewRule = () => {
    const newRuleWithId: ReminderRule = {
      ...newRule,
      id: `rule-${Date.now()}`,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    setRules([...rules, newRuleWithId]);
    setShowNewRuleForm(false);

    // Reset new rule form
    setNewRule({
      daysFromDue: -7,
      isActive: true,
      channels: {
        email: {
          enabled: true,
          subject: "Recordatorio de pago",
          body: "Estimado cliente,\n\nEste es un recordatorio de tu pago próximo.\n\nSaludos cordiales,\nEquipo Financiero",
        },
        whatsapp: {
          enabled: false,
          message: "Recordatorio: Tienes un pago próximo.",
        },
      },
      sendTime: { hour: 9, minute: 0 },
    });

    toast({
      title: "Regla creada",
      description:
        "La nueva regla de recordatorio ha sido creada correctamente.",
    });
  };

  // Update a rule's active state
  const toggleRuleActive = (ruleId: string, isActive: boolean) => {
    setRules(
      rules.map((rule) =>
        rule.id === ruleId ? { ...rule, isActive, updatedAt: new Date() } : rule
      )
    );
  };

  // Format time for display
  const formatTime = (time: ReminderRuleTime) => {
    return `${time.hour.toString().padStart(2, "0")}:${time.minute.toString().padStart(2, "0")}`;
  };

  // Helper to update rule field
  const updateRule = (ruleId: string, updates: Partial<ReminderRule>) => {
    setRules(
      rules.map((rule) =>
        rule.id === ruleId
          ? { ...rule, ...updates, updatedAt: new Date() }
          : rule
      )
    );
  };

  // Helper to update new rule field
  const updateNewRule = (
    updates: Partial<Omit<ReminderRule, "id" | "createdAt" | "updatedAt">>
  ) => {
    setNewRule({ ...newRule, ...updates });
  };

  // Format days relative to due date
  const formatDaysFromDue = (days: number) => {
    if (days < 0) {
      return `${Math.abs(days)} días antes del vencimiento`;
    } else if (days === 0) {
      return "El día del vencimiento";
    } else {
      return `${days} días después del vencimiento`;
    }
  };

  // Render rule item
  const renderRuleItem = (rule: ReminderRule) => {
    const isExpanded = expandedRuleId === rule.id;
    const isEditMode = isEditing === rule.id;

    return (
      <Card
        key={rule.id}
        className={cn(
          "mb-4 border",
          !rule.isActive && "bg-gray-50 opacity-70",
          isExpanded && "ring-1 ring-primary/20"
        )}
      >
        <CardHeader className="p-4 pb-2">
          <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
            <div className="w-full flex-1">
              <div className="mb-3 min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="text-base font-medium">
                    {formatDaysFromDue(rule.daysFromDue)}
                  </h3>
                  {rule.daysFromDue < 0 && (
                    <Badge
                      variant="outline"
                      className="border-blue-200 bg-blue-50 text-blue-700"
                    >
                      Preventivo
                    </Badge>
                  )}
                  {rule.daysFromDue > 0 && (
                    <Badge
                      variant="outline"
                      className="border-amber-200 bg-amber-50 text-amber-700"
                    >
                      Post vencimiento
                    </Badge>
                  )}
                  {rule.daysFromDue === 0 && (
                    <Badge
                      variant="outline"
                      className="border-purple-200 bg-purple-50 text-purple-700"
                    >
                      Día de vencimiento
                    </Badge>
                  )}
                </div>
                <div className="mt-1 flex flex-wrap gap-4 text-sm text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Clock className="h-3.5 w-3.5" />
                    <span>{formatTime(rule.sendTime)}</span>
                  </div>
                  {rule.channels.email.enabled && (
                    <div className="flex items-center gap-1">
                      <Mail className="h-3.5 w-3.5" />
                      <span>Email</span>
                    </div>
                  )}
                  {rule.channels.whatsapp.enabled && (
                    <div className="flex items-center gap-1">
                      <MessageSquare className="h-3.5 w-3.5" />
                      <span>WhatsApp</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center justify-between gap-2 sm:justify-start">
                <div className="flex gap-2">
                  {!isEditMode && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => startEditing(rule.id)}
                      className="hidden sm:flex"
                    >
                      <Edit className="mr-1 h-4 w-4" />
                      <span>Editar</span>
                    </Button>
                  )}
                  {!isEditMode && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => startEditing(rule.id)}
                      className="sm:hidden"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                  )}
                  {isEditMode && (
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => saveRule(rule.id)}
                      className="hidden sm:flex"
                    >
                      <Save className="mr-1 h-4 w-4" />
                      <span>Guardar</span>
                    </Button>
                  )}
                  {isEditMode && (
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => saveRule(rule.id)}
                      className="sm:hidden"
                    >
                      <Save className="h-4 w-4" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="hidden text-destructive hover:bg-destructive/10 hover:text-destructive/80 sm:flex"
                    onClick={() => deleteRule(rule.id)}
                  >
                    <Trash2 className="mr-1 h-4 w-4" />
                    <span>Eliminar</span>
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:bg-destructive/10 hover:text-destructive/80 sm:hidden"
                    onClick={() => deleteRule(rule.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex items-center gap-2 sm:hidden">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => toggleRuleExpansion(rule.id)}
                  >
                    {isExpanded ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </Button>
                  <Switch
                    checked={rule.isActive}
                    onCheckedChange={(checked) =>
                      toggleRuleActive(rule.id, checked)
                    }
                  />
                </div>
              </div>
            </div>
            <div className="hidden items-center gap-2 sm:flex">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => toggleRuleExpansion(rule.id)}
              >
                {isExpanded ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </Button>
              <Switch
                checked={rule.isActive}
                onCheckedChange={(checked) =>
                  toggleRuleActive(rule.id, checked)
                }
              />
            </div>
          </div>
        </CardHeader>

        {isExpanded && (
          <CardContent className="p-4 pt-2">
            <div className="mt-4">
              <Tabs
                defaultValue={
                  rule.channels.email.enabled
                    ? "email"
                    : rule.channels.whatsapp.enabled
                      ? "whatsapp"
                      : "settings"
                }
                className="w-full"
              >
                <TabsList className="mb-4">
                  <TabsTrigger value="settings">Configuración</TabsTrigger>
                  {(rule.channels.email.enabled || isEditMode) && (
                    <TabsTrigger
                      value="email"
                      disabled={!rule.channels.email.enabled && !isEditMode}
                      className={
                        !rule.channels.email.enabled && !isEditMode
                          ? "opacity-50"
                          : ""
                      }
                    >
                      Email
                    </TabsTrigger>
                  )}
                  {(rule.channels.whatsapp.enabled || isEditMode) && (
                    <TabsTrigger
                      value="whatsapp"
                      disabled={!rule.channels.whatsapp.enabled && !isEditMode}
                      className={
                        !rule.channels.whatsapp.enabled && !isEditMode
                          ? "opacity-50"
                          : ""
                      }
                    >
                      WhatsApp
                    </TabsTrigger>
                  )}
                </TabsList>

                <TabsContent value="settings">
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div>
                        <Label htmlFor={`daysFromDue-${rule.id}`}>
                          Días relativos al vencimiento
                        </Label>
                        <div className="mt-1.5 flex items-center">
                          <Select
                            value={
                              rule.daysFromDue < 0
                                ? "before"
                                : rule.daysFromDue > 0
                                  ? "after"
                                  : "same"
                            }
                            onValueChange={(value) => {
                              if (isEditMode) {
                                const currentAbs = Math.abs(rule.daysFromDue);
                                const newDays =
                                  value === "before"
                                    ? -currentAbs
                                    : value === "after"
                                      ? currentAbs
                                      : 0;
                                updateRule(rule.id, { daysFromDue: newDays });
                              }
                            }}
                            disabled={!isEditMode}
                          >
                            <SelectTrigger className="w-[110px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="before">Antes de</SelectItem>
                              <SelectItem value="same">El mismo día</SelectItem>
                              <SelectItem value="after">Después de</SelectItem>
                            </SelectContent>
                          </Select>

                          {rule.daysFromDue !== 0 && (
                            <Input
                              id={`daysFromDue-${rule.id}`}
                              type="number"
                              min="1"
                              max="90"
                              value={Math.abs(rule.daysFromDue)}
                              onChange={(e) => {
                                if (isEditMode) {
                                  const value = parseInt(e.target.value) || 1;
                                  const factor = rule.daysFromDue < 0 ? -1 : 1;
                                  updateRule(rule.id, {
                                    daysFromDue: value * factor,
                                  });
                                }
                              }}
                              className="ml-2 w-20"
                              disabled={!isEditMode}
                            />
                          )}
                          <span className="ml-2">día(s)</span>
                        </div>
                      </div>

                      <div>
                        <Label htmlFor={`sendTime-${rule.id}`}>
                          Hora de envío
                        </Label>
                        <div className="mt-1.5 flex items-center gap-2">
                          <Input
                            id={`sendTime-${rule.id}`}
                            type="time"
                            value={`${rule.sendTime.hour.toString().padStart(2, "0")}:${rule.sendTime.minute.toString().padStart(2, "0")}`}
                            onChange={(e) => {
                              if (isEditMode) {
                                const [hours, minutes] =
                                  e.target.value.split(":");
                                updateRule(rule.id, {
                                  sendTime: {
                                    hour: parseInt(hours) || 0,
                                    minute: parseInt(minutes) || 0,
                                  },
                                });
                              }
                            }}
                            className="w-32"
                            disabled={!isEditMode}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <Label>Canales de notificación</Label>
                      <div className="flex flex-col gap-3">
                        <div className="flex items-center gap-2">
                          <Switch
                            id={`email-enabled-${rule.id}`}
                            checked={rule.channels.email.enabled}
                            onCheckedChange={(checked) => {
                              if (isEditMode) {
                                updateRule(rule.id, {
                                  channels: {
                                    ...rule.channels,
                                    email: {
                                      ...rule.channels.email,
                                      enabled: checked,
                                    },
                                  },
                                });
                              }
                            }}
                            disabled={!isEditMode}
                          />
                          <Label
                            htmlFor={`email-enabled-${rule.id}`}
                            className="cursor-pointer"
                          >
                            <div className="flex items-center gap-1.5">
                              <Mail className="h-4 w-4" />
                              <span>Email</span>
                            </div>
                          </Label>
                        </div>

                        <div className="flex items-center gap-2">
                          <Switch
                            id={`whatsapp-enabled-${rule.id}`}
                            checked={rule.channels.whatsapp.enabled}
                            onCheckedChange={(checked) => {
                              if (isEditMode) {
                                updateRule(rule.id, {
                                  channels: {
                                    ...rule.channels,
                                    whatsapp: {
                                      ...rule.channels.whatsapp,
                                      enabled: checked,
                                    },
                                  },
                                });
                              }
                            }}
                            disabled={!isEditMode}
                          />
                          <Label
                            htmlFor={`whatsapp-enabled-${rule.id}`}
                            className="cursor-pointer"
                          >
                            <div className="flex items-center gap-1.5">
                              <MessageSquare className="h-4 w-4" />
                              <span>WhatsApp</span>
                            </div>
                          </Label>
                        </div>
                      </div>
                    </div>
                  </div>
                </TabsContent>

                {(rule.channels.email.enabled || isEditMode) && (
                  <TabsContent value="email">
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor={`email-subject-${rule.id}`}>
                          Asunto del email
                        </Label>
                        <Input
                          id={`email-subject-${rule.id}`}
                          value={rule.channels.email.subject}
                          onChange={(e) => {
                            if (isEditMode) {
                              updateRule(rule.id, {
                                channels: {
                                  ...rule.channels,
                                  email: {
                                    ...rule.channels.email,
                                    subject: e.target.value,
                                  },
                                },
                              });
                            }
                          }}
                          className="mt-1.5"
                          disabled={!isEditMode}
                        />
                      </div>

                      <div>
                        <Label htmlFor={`email-body-${rule.id}`}>
                          Contenido del email
                        </Label>
                        <Tabs defaultValue="text" className="mt-1.5">
                          <TabsList className="mb-2">
                            <TabsTrigger value="text">Texto</TabsTrigger>
                            <TabsTrigger value="builder">
                              Editor Visual
                            </TabsTrigger>
                          </TabsList>
                          <TabsContent value="text">
                            <Textarea
                              id={`email-body-${rule.id}`}
                              value={rule.channels.email.body}
                              onChange={(e) => {
                                if (isEditMode) {
                                  updateRule(rule.id, {
                                    channels: {
                                      ...rule.channels,
                                      email: {
                                        ...rule.channels.email,
                                        body: e.target.value,
                                      },
                                    },
                                  });
                                }
                              }}
                              className="min-h-[150px]"
                              disabled={!isEditMode}
                            />
                          </TabsContent>
                          <TabsContent value="builder">
                            <div className="rounded-md border bg-gray-50 p-4">
                              <EmailTemplateBuilder
                                onSave={(html) => {
                                  if (isEditMode) {
                                    updateRule(rule.id, {
                                      channels: {
                                        ...rule.channels,
                                        email: {
                                          ...rule.channels.email,
                                          body: html,
                                        },
                                      },
                                    });
                                  }
                                }}
                              />
                            </div>
                          </TabsContent>
                        </Tabs>
                      </div>

                      <div className="text-xs text-muted-foreground">
                        <p className="mb-1 font-medium">
                          Variables disponibles:
                        </p>
                        <ul className="list-inside list-disc space-y-1">
                          <li>
                            <code>{"{{clientName}}"}</code> - Nombre del cliente
                          </li>
                          <li>
                            <code>{"{{dueDate}}"}</code> - Fecha de vencimiento
                          </li>
                          <li>
                            <code>{"{{amount}}"}</code> - Monto a pagar
                          </li>
                          <li>
                            <code>{"{{invoiceNumber}}"}</code> - Número de
                            factura
                          </li>
                        </ul>
                      </div>
                    </div>
                  </TabsContent>
                )}

                {(rule.channels.whatsapp.enabled || isEditMode) && (
                  <TabsContent value="whatsapp">
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor={`whatsapp-message-${rule.id}`}>
                          Mensaje de WhatsApp
                        </Label>
                        <Textarea
                          id={`whatsapp-message-${rule.id}`}
                          value={rule.channels.whatsapp.message}
                          onChange={(e) => {
                            if (isEditMode) {
                              updateRule(rule.id, {
                                channels: {
                                  ...rule.channels,
                                  whatsapp: {
                                    ...rule.channels.whatsapp,
                                    message: e.target.value,
                                  },
                                },
                              });
                            }
                          }}
                          className="mt-1.5 min-h-[150px]"
                          disabled={!isEditMode}
                        />
                      </div>

                      <div className="text-xs text-muted-foreground">
                        <p className="mb-1 font-medium">
                          Variables disponibles:
                        </p>
                        <ul className="list-inside list-disc space-y-1">
                          <li>
                            <code>{"{{clientName}}"}</code> - Nombre del cliente
                          </li>
                          <li>
                            <code>{"{{dueDate}}"}</code> - Fecha de vencimiento
                          </li>
                          <li>
                            <code>{"{{amount}}"}</code> - Monto a pagar
                          </li>
                          <li>
                            <code>{"{{invoiceNumber}}"}</code> - Número de
                            factura
                          </li>
                        </ul>
                      </div>

                      <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm">
                        <p className="text-amber-800">
                          <strong>Nota:</strong> Los mensajes de WhatsApp deben
                          ser concisos y tener un máximo de 1000 caracteres.
                        </p>
                      </div>
                    </div>
                  </TabsContent>
                )}
              </Tabs>
            </div>
          </CardContent>
        )}
      </Card>
    );
  };

  // Render new rule form
  const renderNewRuleForm = () => {
    return (
      <Card className="mb-4 border-2 border-dashed border-primary/30 bg-primary/5">
        <CardHeader className="p-4 pb-2">
          <CardTitle className="text-lg">Nueva regla de recordatorio</CardTitle>
          <CardDescription>
            Configura los parámetros para la nueva regla
          </CardDescription>
        </CardHeader>
        <CardContent className="p-4 pt-2">
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <Label htmlFor="new-daysFromDue">
                  Días relativos al vencimiento
                </Label>
                <div className="mt-1.5 flex items-center">
                  <Select
                    value={
                      newRule.daysFromDue < 0
                        ? "before"
                        : newRule.daysFromDue > 0
                          ? "after"
                          : "same"
                    }
                    onValueChange={(value) => {
                      const currentAbs = Math.abs(newRule.daysFromDue);
                      const newDays =
                        value === "before"
                          ? -currentAbs
                          : value === "after"
                            ? currentAbs
                            : 0;
                      updateNewRule({ daysFromDue: newDays });
                    }}
                  >
                    <SelectTrigger className="w-[110px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="before">Antes de</SelectItem>
                      <SelectItem value="same">El mismo día</SelectItem>
                      <SelectItem value="after">Después de</SelectItem>
                    </SelectContent>
                  </Select>

                  {newRule.daysFromDue !== 0 && (
                    <Input
                      id="new-daysFromDue"
                      type="number"
                      min="1"
                      max="90"
                      value={Math.abs(newRule.daysFromDue)}
                      onChange={(e) => {
                        const value = parseInt(e.target.value) || 1;
                        const factor = newRule.daysFromDue < 0 ? -1 : 1;
                        updateNewRule({ daysFromDue: value * factor });
                      }}
                      className="ml-2 w-20"
                    />
                  )}
                  <span className="ml-2">día(s)</span>
                </div>
              </div>

              <div>
                <Label htmlFor="new-sendTime">Hora de envío</Label>
                <div className="mt-1.5 flex items-center gap-2">
                  <Input
                    id="new-sendTime"
                    type="time"
                    value={`${newRule.sendTime.hour.toString().padStart(2, "0")}:${newRule.sendTime.minute.toString().padStart(2, "0")}`}
                    onChange={(e) => {
                      const [hours, minutes] = e.target.value.split(":");
                      updateNewRule({
                        sendTime: {
                          hour: parseInt(hours) || 0,
                          minute: parseInt(minutes) || 0,
                        },
                      });
                    }}
                    className="w-32"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <Label>Canales de notificación</Label>
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-2">
                  <Switch
                    id="new-email-enabled"
                    checked={newRule.channels.email.enabled}
                    onCheckedChange={(checked) => {
                      updateNewRule({
                        channels: {
                          ...newRule.channels,
                          email: {
                            ...newRule.channels.email,
                            enabled: checked,
                          },
                        },
                      });
                    }}
                  />
                  <Label htmlFor="new-email-enabled" className="cursor-pointer">
                    <div className="flex items-center gap-1.5">
                      <Mail className="h-4 w-4" />
                      <span>Email</span>
                    </div>
                  </Label>
                </div>

                <div className="flex items-center gap-2">
                  <Switch
                    id="new-whatsapp-enabled"
                    checked={newRule.channels.whatsapp.enabled}
                    onCheckedChange={(checked) => {
                      updateNewRule({
                        channels: {
                          ...newRule.channels,
                          whatsapp: {
                            ...newRule.channels.whatsapp,
                            enabled: checked,
                          },
                        },
                      });
                    }}
                  />
                  <Label
                    htmlFor="new-whatsapp-enabled"
                    className="cursor-pointer"
                  >
                    <div className="flex items-center gap-1.5">
                      <MessageSquare className="h-4 w-4" />
                      <span>WhatsApp</span>
                    </div>
                  </Label>
                </div>
              </div>
            </div>

            <Accordion type="single" collapsible>
              <AccordionItem
                value="email"
                disabled={!newRule.channels.email.enabled}
                className={!newRule.channels.email.enabled ? "opacity-50" : ""}
              >
                <AccordionTrigger
                  disabled={!newRule.channels.email.enabled}
                  className={
                    !newRule.channels.email.enabled ? "cursor-not-allowed" : ""
                  }
                >
                  Configuración de Email
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-4 pt-2">
                    <div>
                      <Label htmlFor="new-email-subject">
                        Asunto del email
                      </Label>
                      <Input
                        id="new-email-subject"
                        value={newRule.channels.email.subject}
                        onChange={(e) => {
                          updateNewRule({
                            channels: {
                              ...newRule.channels,
                              email: {
                                ...newRule.channels.email,
                                subject: e.target.value,
                              },
                            },
                          });
                        }}
                        className="mt-1.5"
                      />
                    </div>

                    <div>
                      <Label htmlFor="new-email-body">
                        Contenido del email
                      </Label>
                      <Tabs defaultValue="text" className="mt-1.5">
                        <TabsList className="mb-2">
                          <TabsTrigger value="text">Texto</TabsTrigger>
                          <TabsTrigger value="builder">
                            Editor Visual
                          </TabsTrigger>
                        </TabsList>
                        <TabsContent value="text">
                          <Textarea
                            id="new-email-body"
                            value={newRule.channels.email.body}
                            onChange={(e) => {
                              updateNewRule({
                                channels: {
                                  ...newRule.channels,
                                  email: {
                                    ...newRule.channels.email,
                                    body: e.target.value,
                                  },
                                },
                              });
                            }}
                            className="min-h-[150px]"
                          />
                        </TabsContent>
                        <TabsContent value="builder">
                          <div className="rounded-md border bg-gray-50 p-4">
                            <EmailTemplateBuilder
                              onSave={(html) => {
                                updateNewRule({
                                  channels: {
                                    ...newRule.channels,
                                    email: {
                                      ...newRule.channels.email,
                                      body: html,
                                    },
                                  },
                                });
                              }}
                            />
                          </div>
                        </TabsContent>
                      </Tabs>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem
                value="whatsapp"
                disabled={!newRule.channels.whatsapp.enabled}
                className={
                  !newRule.channels.whatsapp.enabled ? "opacity-50" : ""
                }
              >
                <AccordionTrigger
                  disabled={!newRule.channels.whatsapp.enabled}
                  className={
                    !newRule.channels.whatsapp.enabled
                      ? "cursor-not-allowed"
                      : ""
                  }
                >
                  Configuración de WhatsApp
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-4 pt-2">
                    <div>
                      <Label htmlFor="new-whatsapp-message">
                        Mensaje de WhatsApp
                      </Label>
                      <Textarea
                        id="new-whatsapp-message"
                        value={newRule.channels.whatsapp.message}
                        onChange={(e) => {
                          updateNewRule({
                            channels: {
                              ...newRule.channels,
                              whatsapp: {
                                ...newRule.channels.whatsapp,
                                message: e.target.value,
                              },
                            },
                          });
                        }}
                        className="mt-1.5 min-h-[150px]"
                      />
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </CardContent>
        <CardFooter className="flex justify-end gap-2 p-4">
          <Button variant="outline" onClick={() => setShowNewRuleForm(false)}>
            Cancelar
          </Button>
          <Button
            onClick={addNewRule}
            disabled={
              (!newRule.channels.email.enabled &&
                !newRule.channels.whatsapp.enabled) ||
              (newRule.channels.email.enabled &&
                (!newRule.channels.email.subject ||
                  !newRule.channels.email.body)) ||
              (newRule.channels.whatsapp.enabled &&
                !newRule.channels.whatsapp.message)
            }
          >
            Crear regla
          </Button>
        </CardFooter>
      </Card>
    );
  };

  return (
    <section>
      <div className="mb-6">
        <h2 className="text-lg font-semibold">
          Reglas de recordatorio de pagos
        </h2>
        <p className="text-sm text-gray-500">
          Configura cuándo y cómo se enviarán recordatorios de pago a los
          clientes
        </p>
      </div>

      <div className="space-y-2">
        {rules.length === 0 && !showNewRuleForm && (
          <div className="rounded-lg border bg-gray-50 p-8 text-center">
            <p className="mb-4 text-muted-foreground">
              No hay reglas configuradas. Crea una nueva regla para comenzar.
            </p>
          </div>
        )}

        {rules.map(renderRuleItem)}

        {showNewRuleForm && renderNewRuleForm()}

        {!showNewRuleForm && (
          <Button
            variant="outline"
            onClick={() => setShowNewRuleForm(true)}
            className="w-full border-dashed py-6"
          >
            <PlusCircle className="mr-2 h-4 w-4" />
            Agregar nueva regla
          </Button>
        )}
      </div>
    </section>
  );
}
